/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { encryptData, tryDecryptData, isLegacyFormat } from '@/shared/utils/encryption';

interface ApiKeyContextType {
    geminiKey: string | null;
    openaiKey: string | null;
    hasKeys: boolean;
    isLoading: boolean;
    saveGeminiKey: (key: string) => Promise<void>;
    saveOpenAIKey: (key: string) => Promise<void>;
    removeKeys: () => void;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

export const ApiKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [geminiKey, setGeminiKey] = useState<string | null>(null);
    const [openaiKey, setOpenaiKey] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Load stored keys on mount
    useEffect(() => {
        const loadKeys = async () => {
            if (typeof window === 'undefined') {
                setIsLoading(false);
                return;
            }

            // Load Gemini key
            const encryptedGemini = window.localStorage.getItem('voxpop_gemini_key');
            if (encryptedGemini) {
                // Check for legacy format and clear it
                if (isLegacyFormat(encryptedGemini)) {
                    console.warn('[ApiKeyContext] Legacy encryption format detected for Gemini key. Please re-enter your API key.');
                    window.localStorage.removeItem('voxpop_gemini_key');
                } else {
                    const decrypted = await tryDecryptData(encryptedGemini);
                    if (decrypted) {
                        setGeminiKey(decrypted);
                    } else {
                        window.localStorage.removeItem('voxpop_gemini_key');
                    }
                }
            }

            // Load OpenAI key
            const encryptedOpenai = window.localStorage.getItem('voxpop_openai_key');
            if (encryptedOpenai) {
                // Check for legacy format and clear it
                if (isLegacyFormat(encryptedOpenai)) {
                    console.warn('[ApiKeyContext] Legacy encryption format detected for OpenAI key. Please re-enter your API key.');
                    window.localStorage.removeItem('voxpop_openai_key');
                } else {
                    const decrypted = await tryDecryptData(encryptedOpenai);
                    if (decrypted) {
                        setOpenaiKey(decrypted);
                    } else {
                        window.localStorage.removeItem('voxpop_openai_key');
                    }
                }
            }

            setIsLoading(false);
        };

        loadKeys();
    }, []);

    const saveGeminiKey = async (key: string) => {
        try {
            const encrypted = await encryptData(key);
            localStorage.setItem('voxpop_gemini_key', encrypted);
            setGeminiKey(key);
        } catch (error) {
            console.error('[ApiKeyContext] Failed to save Gemini key:', error);
        }
    };

    const saveOpenAIKey = async (key: string) => {
        try {
            const encrypted = await encryptData(key);
            localStorage.setItem('voxpop_openai_key', encrypted);
            setOpenaiKey(key);
        } catch (error) {
            console.error('[ApiKeyContext] Failed to save OpenAI key:', error);
        }
    };

    const removeKeys = () => {
        localStorage.removeItem('voxpop_gemini_key');
        localStorage.removeItem('voxpop_openai_key');
        setGeminiKey(null);
        setOpenaiKey(null);
    };

    const hasKeys = !!geminiKey || !!openaiKey;

    return (
        <ApiKeyContext.Provider value={{ geminiKey, openaiKey, hasKeys, isLoading, saveGeminiKey, saveOpenAIKey, removeKeys }}>
            {children}
        </ApiKeyContext.Provider>
    );
};

export const useApiKeys = () => {
    const context = useContext(ApiKeyContext);
    if (context === undefined) {
        throw new Error('useApiKeys must be used within an ApiKeyProvider');
    }
    return context;
};
